import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  IconButton,
  Box,
  Paper,
  Typography,
  DialogContentText,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDispatch, useSelector } from 'react-redux';
import { getAdditionalServices, addAdditionalService } from '../apis/additionalServiceSlice';
import SearchClinic from './Autocomplete/SearchClinic';
import { toast } from 'react-toastify';
import { isAdmin, isDoctor } from '../utils/findUserRole';

const createFilterOptions = () => {
  return (options, params) => {
    const filtered = options.filter((opt) => {
      const label = (opt.name || '').toLowerCase();
      return label.includes((params.inputValue || '').toLowerCase());
    });

    if (params.inputValue !== '' && !filtered.some((o) => o.name.toLowerCase() === params.inputValue.toLowerCase())) {
      filtered.push({
        inputValue: params.inputValue,
        name: `Add "${params.inputValue}"`,
        __isNew: true,
      });
    }

    return filtered;
  };
};

export default function AdditionalServiceDialog({
  open,
  onClose,
  additionalServices = [],
  setAdditionalServices,
  inline = false,
  clinicId: propClinicId = null,
}) {
  const dispatch = useDispatch();
  const { services } = useSelector((s) => s.additionalServiceData || { services: [] });
  const { loggedIn } = useSelector((state) => state.authData || {});
   

  const [rows, setRows] = useState(
    (additionalServices || []).map((s, idx) => ({ id: idx + 1, order: s.order ?? idx + 1, ...s })),
  );

  const [selectedService, setSelectedService] = useState(null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [editIndex, setEditIndex] = useState(-1);
  const [autoKey, setAutoKey] = useState(0);


  // For create dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createServiceName, setCreateServiceName] = useState('');
  const [createClinicData, setCreateClinicData] = useState({ clinicId: propClinicId || loggedIn?.user?.clinicId || null });

  useEffect(() => {
    dispatch(getAdditionalServices());
  }, [dispatch]);

  useEffect(() => {
    setRows((additionalServices || []).map((s, idx) => ({ id: idx + 1, order: s.order ?? idx + 1, ...s })));
  }, [additionalServices]);

  // Autofill clinic when create dialog opens if user has clinicId
  useEffect(() => {
     if (!(isAdmin() || isDoctor())) return;
    if (createDialogOpen && !propClinicId) {
      const userClinicId = loggedIn?.clinicId;
      if (userClinicId) {
        setCreateClinicData({ clinicId: userClinicId });
      }
    }
  }, [createDialogOpen, propClinicId, loggedIn?.clinicId]);

  const reset = () => {
    setSelectedService(null);
    setAmount('');
    setNote('');
    setEditIndex(-1);
  };

  const handleServiceChange = (event, newValue) => {
    if (typeof newValue === 'string') {
      // User typed and pressed Enter
      setTimeout(() => {
        setCreateServiceName(newValue);
        setCreateDialogOpen(true);
      });
    } else if (newValue && newValue.__isNew) {
      // User clicked "Add ..." option
      setCreateServiceName(newValue.inputValue || '');
      setCreateDialogOpen(true);
    } else if (newValue && newValue.inputValue) {
      // Free solo with inputValue
      setCreateServiceName(newValue.inputValue);
      setCreateDialogOpen(true);
    } else if (newValue) {
      // Existing service selected
      setSelectedService(newValue);
    } else {
      setSelectedService(null);
    }
  };

  const handleCreateServiceSubmit = async (event) => {
    event.preventDefault();

    if (!createServiceName.trim()) {
      toast.error('Service name is required');
      return;
    }

    if (!createClinicData.clinicId) {
      toast.error('Clinic is required');
      return;
    }

    const payload = {
      name: createServiceName,
      clinicId: createClinicData.clinicId,
    };

    const response = await dispatch(addAdditionalService(payload));

    if (response?.payload?.success) {
      toast.success('Service created successfully');
      // Refresh services list
      await dispatch(getAdditionalServices());
      // Set the newly created service as selected
      setSelectedService(response.payload.data);
      setAutoKey((k) => k + 1);
      handleCloseCreateDialog();
    } else {
      toast.error(response?.payload?.message || 'Error creating service');
    }
  };

  const handleCloseCreateDialog = () => {
    setCreateServiceName('');
    setCreateClinicData({ clinicId: propClinicId || loggedIn?.user?.clinicId || null });
    setCreateDialogOpen(false);
  };

  const handleAdd = () => {
    if (!selectedService || !selectedService._id) {
      toast.error('Please select a service');
      return;
    }

    // Prevent duplicates (ignore current edit index)
    const isDuplicate = rows.some((r, idx) => {
      if (editIndex > -1 && idx === editIndex) return false;
      return String(r.serviceId) === String(selectedService._id);
    });

    if (isDuplicate) {
      toast.error('Service already added');
      return;
    }

    const item = {
      serviceId: selectedService._id,
      name: selectedService.name,
      amount: amount ? Number(amount) : 0,
      note: note || null,
      order: rows.length + 1,
    };

    let newRows = [];
    if (editIndex > -1) {
      newRows = rows.map((r, idx) => (idx === editIndex ? { ...r, ...item } : r));
    } else {
      newRows = [...rows, { id: rows.length + 1, ...item }];
    }

    newRows = newRows.map((r, idx) => ({ ...r, id: idx + 1, order: idx + 1 }));
    setRows(newRows);
    setAdditionalServices(
      newRows.map((r) => ({ serviceId: r.serviceId, name: r.name, amount: r.amount, note: r.note, order: r.order })),
    );
    reset();
    setSelectedService(null);
    setAutoKey((k) => k + 1); 
  };

  const handleEdit = (row) => {
    const idx = rows.findIndex((r) => r.id === row.id);
    if (idx === -1) return;
    setEditIndex(idx);
    const svc = services.find((s) => String(s._id) === String(row.serviceId));
    setSelectedService(svc || null);
    setAmount(row.amount || '');
    setNote(row.note || '');
  };

  const handleDelete = (row) => {
    const newRows = rows.filter((r) => r.id !== row.id).map((r, idx) => ({ ...r, id: idx + 1, order: idx + 1 }));
    setRows(newRows);
    setAdditionalServices(
      newRows.map((r) => ({ serviceId: r.serviceId, name: r.name, amount: r.amount, note: r.note, order: r.order })),
    );
  };

  const filterOptions = createFilterOptions();

  // Deduplicate services by _id to avoid showing same service twice
  const uniqueServices = Array.from(
    new Map((services || []).map((s) => [String(s._id), s])).values()
  );

  const panel = (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Autocomplete
        key={autoKey}
          options={uniqueServices}
          getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.name || '')}
          value={selectedService}
          onChange={handleServiceChange}
          freeSolo
          sx={{ width: 320 }}
          filterOptions={filterOptions}
          renderInput={(params) => <TextField {...params} label="Select or Create Service" />}
        />

        <TextField
          label="Amount"
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          sx={{ width: 140 }}
        />

        <TextField
          label="Note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          sx={{ minWidth: 220, flex: '1 1 220px' }}
        />

        <Button variant="contained" onClick={handleAdd}>
          {editIndex > -1 ? 'Update' : 'Add'}
        </Button>
      </Box>

      <Paper elevation={1} sx={{ display: 'flex', alignItems: 'center', p: 1, mb: 1, backgroundColor: '#f5f5f5', fontWeight: 600 }}>
        <Typography sx={{ width: 40 }}>#</Typography>
        <Typography sx={{ width: 240 }}>Service</Typography>
        <Typography sx={{ width: 120 }}>Amount</Typography>
        <Typography sx={{ flex: 1 }}>Note</Typography>
        <Typography sx={{ width: 140, textAlign: 'center' }}>Actions</Typography>
      </Paper>

      <Box>
        {rows.map((row) => (
          <Paper key={row.id} sx={{ display: 'flex', alignItems: 'center', p: 1, mb: 1 }}>
            <Typography sx={{ width: 40 }}>{row.order}</Typography>
            <Typography sx={{ width: 240 }}>{row.name}</Typography>
            <Typography sx={{ width: 120 }}>{row.amount}</Typography>
            <Typography sx={{ flex: 1 }}>{row.note || '-'}</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, width: 140, justifyContent: 'center' }}>
              <IconButton onClick={() => handleEdit(row)} size="small">
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => handleDelete(row)} size="small" color="error">
                <DeleteIcon />
              </IconButton>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Create Service Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog}>
        <form onSubmit={handleCreateServiceSubmit}>
          <DialogTitle>Create New Service</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Enter service details to create a new service.
            </DialogContentText>
            <SearchClinic
              open={createDialogOpen}
              setData={setCreateClinicData}
              data={createClinicData}
              name="clinicId"
              label="Select Clinic"
              variant="standard"
              size="small"
              disabled={!!propClinicId}
            />
            <TextField
              autoFocus
              margin="dense"
              label="Service Name"
              fullWidth
              variant="standard"
              value={createServiceName}
              onChange={(e) => setCreateServiceName(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCreateDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              Create Service
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );

  const handleClose = () => {
    setAdditionalServices(
      rows.map((r) => ({ serviceId: r.serviceId, name: r.name, amount: r.amount, note: r.note, order: r.order })),
    );
    if (onClose) onClose();
  };

  if (inline) return panel;

  return (
    <Dialog open={open} fullWidth maxWidth="md" onClose={handleClose}>
      <DialogTitle>Additional Services</DialogTitle>
      <DialogContent dividers>{panel}</DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="contained">
          Save & Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
